import {
  Box, Card, CardContent, Chip, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EventIcon from '@mui/icons-material/Event';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { EmptyState } from '@/components/EmptyState';
import { TriageBadge } from '@/components/SeverityChip';
import { useApiQuery } from '@/hooks/useApiQuery';
import { clinician } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type {
  AppointmentListItem,
  AppointmentStatus,
  Modality,
  TriageLevel,
} from '@/types/medical';

const ACTIVE_STATUSES: AppointmentStatus[] =
  ['booked', 'confirmed', 'checked_in', 'in_consult'];
const HISTORICAL_STATUSES: AppointmentStatus[] =
  ['completed', 'no_show', 'cancelled_pt', 'cancelled_dr', 'refunded'];

type TabKey = 'active' | 'history';

export function ClinicianAppointmentsPage() {
  const q = useApiQuery({
    queryKey: ['clinician', 'appointments', 'all'],
    queryFn:  () => clinician.appointments.list(),
  });

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    return {
      active: all
        .filter((a) => ACTIVE_STATUSES.includes(a.status))
        .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()),
      history: all
        .filter((a) => HISTORICAL_STATUSES.includes(a.status))
        .sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime()),
    };
  }, [q.data]);

  const [tab, setTab] = useState<TabKey>('active');

  return (
    <>
      <PageHeader title="Appointments" />
      <DataState query={q}>
        {() => (
          <>
            <Tabs
              value={tab} onChange={(_, v: TabKey) => setTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="active"  label={`Active (${buckets.active.length})`} />
              <Tab value="history" label={`History (${buckets.history.length})`} />
            </Tabs>
            {buckets[tab].length === 0 ? (
              <EmptyState
                title={tab === 'active' ? 'No active appointments' : 'No past appointments yet'}
              />
            ) : (
              <Stack spacing={1.5}>
                {buckets[tab].map((a) => <Row key={a.id} appointment={a} />)}
              </Stack>
            )}
          </>
        )}
      </DataState>
    </>
  );
}

function Row({ appointment }: { appointment: AppointmentListItem }) {
  return (
    <Card
      component={RouterLink}
      to={`/clinician/appointments/${appointment.id}`}
      sx={{
        textDecoration: 'none', color: 'inherit',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <ModalityIcon modality={appointment.modality} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {appointment.procedure_name}
              </Typography>
              {appointment.ai_triage_level && (
                <TriageBadge level={appointment.ai_triage_level as TriageLevel} small />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              Patient {appointment.patient_initials}
            </Typography>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 180, textAlign: 'right' }}>
            <Typography variant="body2" className="tabular">
              {formatDateTime(appointment.start_at)}
            </Typography>
            <Chip
              size="small"
              label={appointment.status.replace(/_/g, ' ')}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
          <ChevronRightIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  );
}

function ModalityIcon({ modality }: { modality: Modality }) {
  const icon = {
    in_person: <LocationOnIcon />,
    video:     <VideocamIcon />,
    phone:     <PhoneIcon />,
    async:     <ChatIcon />,
    home:      <HomeWorkIcon />,
    hybrid:    <EventIcon />,
  }[modality];
  return (
    <Box sx={{
      width: 40, height: 40, flexShrink: 0,
      borderRadius: 1.5,
      bgcolor: 'background.subtle', color: 'primary.main',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
  );
}
